// 편집기 리듀서를 감싸는 되돌리기/다시 — 상태 스냅샷을 최대 100단계 쌓는다.

import { editorReducer } from "./reducer";
import { EditorAction, EditorItem, EditorState, EditorTrade } from "./types";

export const HISTORY_LIMIT = 100;

export interface EditorHistory {
  past: EditorState[];
  present: EditorState;
  future: EditorState[];
  /** 직전에 기록한 연속 입력의 묶음 키. 같은 칸에 이어 치면 한 단계로 합친다. */
  coalesce: string | null;
  // 마지막 저장본의 내용 키(contentKeyOf) · null 이면 저장본을 모른다(되살린 초안 등) — dirty 를 내리지 않는다.
  savedKey: string | null;
}

export type HistoryAction =
  | EditorAction
  | { type: "UNDO" }
  | { type: "REDO" }
  /** 연속 입력 묶음을 닫는다. 칸에서 빠져나올 때 보낸다. */
  | { type: "SEAL" }
  /** 여러 편집을 되돌리기 한 단계로 — 값 확정 + 새 줄처럼. */
  | { type: "BATCH"; actions: EditorAction[] }
  /** 마지막 한 단계가 `from` 에서 시작했을 때만 되돌린다 — 토스트의 「되돌리기」 가 그 뒤 편집을 되돌리지 않게. */
  | { type: "UNDO_STEP"; from: EditorState };

/** 마지막 기록 단계가 `from` 에서 출발했고 그 뒤 기록된 편집이 없는가. */
export const isLastStepFrom = (history: EditorHistory, from: EditorState): boolean =>
  history.past[history.past.length - 1] === from && history.present !== from;

export interface HistoryKey {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/** ⌘Z·Ctrl+Z 는 되돌리기, ⌘⇧Z·Ctrl+Y 는 다시. 그 밖은 null(시트 keymap 과 같은 규칙). */
export const historyKeyOf = (e: HistoryKey): "UNDO" | "REDO" | null => {
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return null;
  const key = e.key.toLowerCase();
  if (key === "z") return e.shiftKey ? "REDO" : "UNDO";
  return key === "y" ? "REDO" : null;
};

/** 기록하지 않는 액션. 접기는 편집이 아니고, 저장 응답은 내용을 안 바꾼다. */
const UNRECORDED: ReadonlySet<EditorAction["type"]> = new Set<EditorAction["type"]>([
  "TOGGLE_TRADE",
  "SAVED",
]);

// 키 입력마다 오는 액션의 묶음 키. 같은 키가 이어지면 되돌리기 한 단계로 합친다.
const coalesceKeyOf = (action: EditorAction): string | null => {
  switch (action.type) {
    case "SET_TITLE":
    case "SET_MGMT_PCT":
    case "SET_EXTRA_NOTICE":
    case "SET_AREA_PYEONG":
    case "SET_AREA_M2":
    case "SET_ROUNDING":
      return action.type;
    case "SET_CONDITION":
      return `${action.type}:${action.index}`;
    case "SET_TRADE_NAME":
    case "SET_TRADE_CONDITION":
      return `${action.type}:${action.tradeKey}`;
    case "SET_ITEM":
      return `${action.type}:${action.ref.itemKey}:${Object.keys(action.patch).sort().join(",")}`;
    case "SET_SIMPLE_PRICE":
    case "SET_COST":
    case "SET_MARGIN":
      return `${action.type}:${action.ref.itemKey}`;
    case "SET_TRADE_MARGIN":
      return `${action.type}:${action.tradeKey}`;
    case "SET_PHASE_DURATION":
      return `${action.type}:${action.phase_code}`;
    default:
      return null;
  }
};

// 내용이 아닌 칸은 자리별로만 뺀다 — 최상위의 저장 표시·조건 키 · 공종·항목(자식 포함)의 행 키·접힘·제안 표시.
const TOP_SKIP: ReadonlySet<string> = new Set(["dirty", "saved", "savedKey", "condition_keys"]);
const ROW_SKIP: ReadonlySet<string> = new Set(["key", "collapsed", "suggested"]);
const NO_SKIP: ReadonlySet<string> = new Set();

type Encode = (value: unknown) => string | undefined;

// 객체를 키 정렬 JSON 으로 — skip 은 이 객체 자신의 칸에만 · null·없음은 같게 본다 · nested 는 칸별 인코더.
const encodeObject = (obj: object, skip: ReadonlySet<string>, nested: Record<string, Encode> = {}): string => {
  const parts: string[] = [];
  for (const name of Object.keys(obj).sort()) {
    if (skip.has(name)) continue;
    const raw = (obj as Record<string, unknown>)[name];
    const encoded = raw === null || raw === undefined ? undefined : (nested[name] ?? encode)(raw);
    if (encoded !== undefined) parts.push(`${JSON.stringify(name)}:${encoded}`);
  }
  return `{${parts.join(",")}}`;
};

// 화면용 칸이 없는 값(일정·조건 등) — 깊이와 상관없이 그대로 비교한다.
const encode: Encode = (value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((v) => encode(v) ?? "null").join(",")}]`;
  return encodeObject(value, NO_SKIP);
};

// 상태는 불변이라 공종·항목 객체 참조별로 키를 기억한다 — 키 입력마다 바뀐 공종·항목만 다시 센다.
const itemKeys = new WeakMap<EditorItem, string>();
const tradeKeys = new WeakMap<EditorTrade, string>();
const memo = <T extends object>(cache: WeakMap<T, string>, value: T, make: (value: T) => string): string => {
  const hit = cache.get(value);
  if (hit !== undefined) return hit;
  const made = make(value);
  cache.set(value, made);
  return made;
};
const listOf = <T>(keyOf: (value: T) => string): Encode => (value) => `[${(value as T[]).map(keyOf).join(",")}]`;
const itemKeyOf = (item: EditorItem): string => memo(itemKeys, item, (i) => encodeObject(i, ROW_SKIP, { children: listOf(itemKeyOf) }));
const tradeKeyOf = (trade: EditorTrade): string => memo(tradeKeys, trade, (t) => encodeObject(t, ROW_SKIP, { items: listOf(itemKeyOf) }));

// 원본과 같은지 가르는 내용 키 — 키 순서와 상관없게 정렬해 문자열로 굳힌다.
export const contentKeyOf = (state: EditorState): string => encodeObject(state, TOP_SKIP, { trades: listOf(tradeKeyOf) });

// 저장본과 내용을 맞대 dirty 를 정한다 — 바꿨다가 원래 값으로 돌아오면 clean.
const withDirty = (present: EditorState, savedKey: string | null): EditorState => {
  const dirty = savedKey === null ? present.dirty : contentKeyOf(present) !== savedKey;
  return dirty === present.dirty ? present : { ...present, dirty };
};

export const initHistory = (present: EditorState): EditorHistory => ({
  past: [],
  present,
  future: [],
  coalesce: null,
  savedKey: present.dirty ? null : contentKeyOf(present),
});

// 접힘은 편집이 아니다 — 되돌려도 지금 펼쳐 둔 모양을 그대로 둔다.
const keepCollapsed = (target: EditorState, current: EditorState): EditorState["trades"] => {
  const now = new Map(current.trades.map((t) => [t.key, t.collapsed]));
  return target.trades.map((t) => {
    const collapsed = now.get(t.key);
    return collapsed === undefined || collapsed === t.collapsed ? t : { ...t, collapsed };
  });
};

// 저장본과 내용이 같을 때만 dirty 를 내리고, 마지막 저장 금액은 지금 것을 잇는다.
const revisit = (target: EditorState, history: EditorHistory): EditorState => {
  const next = { ...target, trades: keepCollapsed(target, history.present), saved: history.present.saved };
  return { ...next, dirty: history.savedKey === null || contentKeyOf(next) !== history.savedKey };
};

export const historyReducer = (
  history: EditorHistory,
  action: HistoryAction
): EditorHistory => {
  switch (action.type) {
    case "UNDO": {
      const previous = history.past[history.past.length - 1];
      if (!previous) return history;
      const present = revisit(previous, history);
      return {
        past: history.past.slice(0, -1),
        present,
        future: [history.present, ...history.future],
        coalesce: null,
        savedKey: history.savedKey,
      };
    }
    case "REDO": {
      const [next, ...rest] = history.future;
      if (!next) return history;
      const present = revisit(next, history);
      return {
        past: [...history.past, history.present],
        present,
        future: rest,
        coalesce: null,
        savedKey: history.savedKey,
      };
    }
    case "UNDO_STEP":
      return isLastStepFrom(history, action.from) ? historyReducer(history, { type: "UNDO" }) : history;
    case "SEAL":
      return history.coalesce === null ? history : { ...history, coalesce: null };
    case "RESTORE":
      // 통째로 갈아 끼운 상태 앞의 기록은 다른 문서의 것이다.
      return initHistory(editorReducer(history.present, action));
    case "BATCH": {
      const present = action.actions.reduce(editorReducer, history.present);
      return present === history.present ? history : record(history, withDirty(present, history.savedKey), null);
    }
    default:
      break;
  }

  const reduced = editorReducer(history.present, action);
  if (reduced === history.present) return history;
  if (UNRECORDED.has(action.type)) {
    // 기록 안 하는 액션을 지나면 연속 입력 묶음도 끊는다.
    if (action.type !== "SAVED") return { ...history, present: reduced, coalesce: null };
    return { ...history, present: withDirty(reduced, action.sentKey), coalesce: null, savedKey: action.sentKey };
  }

  const present = withDirty(reduced, history.savedKey);
  const key = coalesceKeyOf(action);
  if (key !== null && key === history.coalesce) {
    return { ...history, present, future: [] };
  }
  return record(history, present, key);
};

// 지금 상태를 과거에 쌓고 새 상태로 — 다시 하기는 비운다.
const record = (
  history: EditorHistory,
  present: EditorState,
  coalesce: string | null
): EditorHistory => {
  const past = [...history.past, history.present];
  return {
    past: past.length > HISTORY_LIMIT ? past.slice(past.length - HISTORY_LIMIT) : past,
    present,
    future: [],
    coalesce,
    savedKey: history.savedKey,
  };
};
